
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

const ProtectionDonneesPage = () => {
  return (
    <>
      <header>
        <h1 className="sr-only">Protection des données - SARL ADF Biomédical Algérie</h1>
      </header>
      <Navbar />
      <main>
        {/* HERO SECTION */}
        <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <p className="text-sm text-text-secondary mb-6">
                <Link to="/" className="hover:text-[#E31B23] transition-colors cursor-pointer">Accueil</Link> / Protection des données
              </p>
              <h2 className="text-5xl md:text-6xl font-bold text-text-primary mb-6">
                Protection des données
              </h2>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto">
                Notre engagement pour la sécurité de vos informations
              </p>
            </motion.div>
          </div>
        </section>

        {/* CONTENU */}
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-12"
            >
              {/* Introduction */}
              <div>
                <div className="bg-gray-50 rounded-xl p-8 space-y-4">
                  <p className="text-base text-text-secondary leading-relaxed">
                    SARL ADF Biomédical accorde la plus haute importance à la protection de vos données personnelles. Cette page détaille les mesures techniques et organisationnelles que nous mettons en œuvre pour garantir la sécurité, la confidentialité et l'intégrité de vos informations.
                  </p>
                  <p className="text-base text-text-secondary leading-relaxed">
                    Notre approche de la protection des données est conforme à la loi algérienne n° 18-07 du 10 juin 2018 relative à la protection des personnes physiques dans le traitement des données à caractère personnel et au Règlement Général sur la Protection des Données (RGPD).
                  </p>
                  <p className="text-base text-text-secondary leading-relaxed">
                    <strong className="text-text-primary">Date de dernière mise à jour :</strong> Janvier 2025
                  </p>
                </div>
              </div>

              {/* Cadre légal */}
              <div>
                <h3 className="text-3xl font-bold text-text-primary mb-6 flex items-center">
                  <i className="ri-scales-line text-[#E31B23] mr-4 w-8 h-8 flex items-center justify-center"></i>
                  Cadre légal
                </h3>
                <div className="bg-gray-50 rounded-xl p-8 space-y-4">
                  <p className="text-base text-text-secondary leading-relaxed mb-4">
                    Notre politique de protection des données s'inscrit dans le respect des réglementations suivantes :
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">Loi algérienne n° 18-07</strong> du 10 juin 2018 relative à la protection des personnes physiques dans le traitement des données à caractère personnel
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">RGPD</strong> (Règlement Général sur la Protection des Données) de l'Union Européenne
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">Normes ISO 27001</strong> relatives à la sécurité de l'information
                    </li>
                  </ul>
                </div>
              </div>

              {/* Principes fondamentaux */}
              <div>
                <h3 className="text-3xl font-bold text-text-primary mb-6 flex items-center">
                  <i className="ri-shield-check-line text-[#E31B23] mr-4 w-8 h-8 flex items-center justify-center"></i>
                  Principes fondamentaux
                </h3>
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-xl p-8">
                    <h4 className="text-xl font-bold text-text-primary mb-4 flex items-center">
                      <i className="ri-eye-line text-[#E31B23] mr-3 w-6 h-6 flex items-center justify-center"></i>
                      1. Transparence
                    </h4>
                    <p className="text-base text-text-secondary leading-relaxed">
                      Nous vous informons clairement sur les données que nous collectons, les raisons de cette collecte, et la manière dont nous les utilisons. Vous avez le droit de savoir ce qui est fait de vos informations personnelles.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-8">
                    <h4 className="text-xl font-bold text-text-primary mb-4 flex items-center">
                      <i className="ri-focus-line text-[#E31B23] mr-3 w-6 h-6 flex items-center justify-center"></i>
                      2. Limitation des finalités
                    </h4>
                    <p className="text-base text-text-secondary leading-relaxed">
                      Vos données sont collectées pour des finalités déterminées, explicites et légitimes. Nous ne les utilisons pas à d'autres fins sans votre consentement préalable.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-8">
                    <h4 className="text-xl font-bold text-text-primary mb-4 flex items-center">
                      <i className="ri-database-line text-[#E31B23] mr-3 w-6 h-6 flex items-center justify-center"></i>
                      3. Minimisation des données
                    </h4>
                    <p className="text-base text-text-secondary leading-relaxed">
                      Nous ne collectons que les données strictement nécessaires aux finalités poursuivies. Aucune information superflue n'est demandée ou conservée.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-8">
                    <h4 className="text-xl font-bold text-text-primary mb-4 flex items-center">
                      <i className="ri-checkbox-line text-[#E31B23] mr-3 w-6 h-6 flex items-center justify-center"></i>
                      4. Exactitude
                    </h4>
                    <p className="text-base text-text-secondary leading-relaxed">
                      Nous veillons à ce que vos données soient exactes et mises à jour. Vous pouvez à tout moment demander la rectification de vos informations.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-8">
                    <h4 className="text-xl font-bold text-text-primary mb-4 flex items-center">
                      <i className="ri-time-line text-[#E31B23] mr-3 w-6 h-6 flex items-center justify-center"></i>
                      5. Limitation de la conservation
                    </h4>
                    <p className="text-base text-text-secondary leading-relaxed">
                      Vos données ne sont conservées que pendant la durée nécessaire aux finalités pour lesquelles elles ont été collectées, conformément aux obligations légales.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-8">
                    <h4 className="text-xl font-bold text-text-primary mb-4 flex items-center">
                      <i className="ri-lock-line text-[#E31B23] mr-3 w-6 h-6 flex items-center justify-center"></i>
                      6. Intégrité et confidentialité
                    </h4>
                    <p className="text-base text-text-secondary leading-relaxed">
                      Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour garantir la sécurité de vos données contre tout accès non autorisé, perte ou destruction.
                    </p>
                  </div>
                </div>
              </div>

              {/* Mesures de sécurité */}
              <div>
                <h3 className="text-3xl font-bold text-text-primary mb-6 flex items-center">
                  <i className="ri-shield-star-line text-[#E31B23] mr-4 w-8 h-8 flex items-center justify-center"></i>
                  Mesures de sécurité techniques
                </h3>
                <div className="bg-gray-50 rounded-xl p-8 space-y-4">
                  <p className="text-base text-text-secondary leading-relaxed mb-4">
                    Nous avons mis en place les mesures de sécurité techniques suivantes :
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">Chiffrement SSL/TLS :</strong> Toutes les communications entre votre navigateur et notre site sont chiffrées
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">Chiffrement des données sensibles :</strong> Les données sensibles sont chiffrées en base de données
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">Pare-feu et protection anti-intrusion :</strong> Systèmes de détection et de prévention des intrusions
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">Sauvegardes régulières :</strong> Sauvegardes automatiques et sécurisées de vos données
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">Surveillance continue :</strong> Monitoring 24/7 pour détecter toute activité suspecte
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">Mises à jour de sécurité :</strong> Application régulière des correctifs de sécurité
                    </li>
                  </ul>
                </div>
              </div>

              {/* Mesures organisationnelles */}
              <div>
                <h3 className="text-3xl font-bold text-text-primary mb-6 flex items-center">
                  <i className="ri-team-line text-[#E31B23] mr-4 w-8 h-8 flex items-center justify-center"></i>
                  Mesures de sécurité organisationnelles
                </h3>
                <div className="bg-gray-50 rounded-xl p-8 space-y-4">
                  <p className="text-base text-text-secondary leading-relaxed mb-4">
                    Au-delà des mesures techniques, nous avons mis en place des procédures organisationnelles strictes :
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">Accès restreint :</strong> Seules les personnes autorisées ont accès aux données personnelles
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">Formation du personnel :</strong> Sensibilisation régulière de nos équipes à la protection des données
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">Clauses de confidentialité :</strong> Tous nos employés sont soumis à des obligations de confidentialité
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">Audits réguliers :</strong> Évaluation périodique de nos pratiques de sécurité
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      <strong className="text-text-primary">Plan de réponse aux incidents :</strong> Procédures établies pour réagir rapidement en cas de violation
                    </li>
                  </ul>
                </div>
              </div>

              {/* Transfert de données */}
              <div>
                <h3 className="text-3xl font-bold text-text-primary mb-6 flex items-center">
                  <i className="ri-global-line text-[#E31B23] mr-4 w-8 h-8 flex items-center justify-center"></i>
                  Transfert de données
                </h3>
                <div className="bg-gray-50 rounded-xl p-8 space-y-4">
                  <p className="text-base text-text-secondary leading-relaxed">
                    Vos données personnelles sont hébergées et traitées en Algérie. Dans le cas où un transfert de données vers un pays tiers serait nécessaire, nous nous assurons que :
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      Le pays destinataire offre un niveau de protection adéquat
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      Des garanties appropriées sont mises en place (clauses contractuelles types)
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      Votre consentement explicite est obtenu si nécessaire
                    </li>
                  </ul>
                </div>
              </div>

              {/* Violation de données */}
              <div>
                <h3 className="text-3xl font-bold text-text-primary mb-6 flex items-center">
                  <i className="ri-alarm-warning-line text-[#E31B23] mr-4 w-8 h-8 flex items-center justify-center"></i>
                  Notification en cas de violation
                </h3>
                <div className="bg-gray-50 rounded-xl p-8 space-y-4">
                  <p className="text-base text-text-secondary leading-relaxed">
                    En cas de violation de données personnelles susceptible d'engendrer un risque élevé pour vos droits et libertés, nous nous engageons à :
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      Notifier l'autorité de contrôle compétente dans les 72 heures
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      Vous informer dans les meilleurs délais de la nature de la violation
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      Vous communiquer les mesures prises pour remédier à la violation
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      Vous conseiller sur les mesures à prendre pour protéger vos intérêts
                    </li>
                  </ul>
                </div>
              </div>

              {/* Vos droits */}
              <div>
                <h3 className="text-3xl font-bold text-text-primary mb-6 flex items-center">
                  <i className="ri-hand-heart-line text-[#E31B23] mr-4 w-8 h-8 flex items-center justify-center"></i>
                  Exercice de vos droits
                </h3>
                <div className="bg-gray-50 rounded-xl p-8 space-y-4">
                  <p className="text-base text-text-secondary leading-relaxed mb-4">
                    Pour exercer vos droits relatifs à la protection de vos données personnelles, vous pouvez :
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      Nous contacter par email : <a href="mailto:contact@adfbiomedical.dz" className="text-[#E31B23] hover:underline cursor-pointer">contact@adfbiomedical.dz</a>
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      Nous appeler au : +213 770 30 79 05
                    </li>
                    <li className="flex items-start text-base text-text-secondary">
                      <i className="ri-checkbox-circle-fill text-[#E31B23] mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                      Nous écrire à : SARL ADF Biomédical, Local N01 Benghazi N16, Baraki, Alger, Algérie
                    </li>
                  </ul>
                  <p className="text-base text-text-secondary leading-relaxed mt-6">
                    Nous nous engageons à répondre à votre demande dans un délai maximum d'un mois à compter de sa réception.
                  </p>
                </div>
              </div>

              {/* Réclamation */}
              <div>
                <h3 className="text-3xl font-bold text-text-primary mb-6 flex items-center">
                  <i className="ri-feedback-line text-[#E31B23] mr-4 w-8 h-8 flex items-center justify-center"></i>
                  Droit de réclamation
                </h3>
                <div className="bg-gray-50 rounded-xl p-8">
                  <p className="text-base text-text-secondary leading-relaxed">
                    Si vous estimez que vos droits en matière de protection des données ne sont pas respectés, vous avez le droit d'introduire une réclamation auprès de l'Autorité Nationale de Protection des Données à Caractère Personnel (ANPDCP) en Algérie ou de l'autorité de contrôle compétente dans votre pays de résidence.
                  </p>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-gradient-to-r from-[#E31B23] to-[#C41820] rounded-2xl p-12 text-center">
                <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-white/20 rounded-full">
                  <i className="ri-shield-check-line text-5xl text-white"></i>
                </div>
                <h4 className="text-3xl font-bold text-white mb-4">Votre sécurité est notre priorité</h4>
                <p className="text-lg text-white/90 mb-8">
                  Pour toute question sur la protection de vos données, contactez-nous
                </p>
                <Link
                  to="/contact"
                  className="inline-block px-10 py-4 bg-white text-[#E31B23] rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg cursor-pointer whitespace-nowrap"
                >
                  Nous contacter
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ProtectionDonneesPage;
